/**
 * Service Health Check
 *
 * Checks if required backend services are running
 */

export interface ServiceHealth {
  isHealthy: boolean
  services: {
    accounting: boolean
    geolocation: boolean
    entity: boolean
    currencyRates: boolean
  }
}

// Use same-origin proxy (nginx routes /api/ and /health to backend)
const API_BASE_URL = '';

/**
 * Check if the accounting server is reachable
 */
async function checkAccountingServer(): Promise<boolean> {
  try {
    // Check the /health endpoint (configured in Nginx at root level)
    const response = await fetch(`${API_BASE_URL}/health`, {
      method: 'GET',
      signal: AbortSignal.timeout(10000), // 10 second timeout (increased for slower loads)
    })
    return response.ok
  } catch (_error) {
    return false
  }
}

/**
 * Check if the geolocation service is reachable through the proxy
 */
async function checkGeolocationService(): Promise<boolean> {
  try {
    // Check geolocation through the accounting server proxy
    const response = await fetch(`${API_BASE_URL}/api/v1/geolocation/countries`, {
      method: 'GET',
      signal: AbortSignal.timeout(20000), // 20 second timeout for slower loads
    })
    return response.ok
  } catch (_error) {
    return false
  }
}

/**
 * Check if the entity service is reachable through the proxy
 */
async function checkEntityService(): Promise<boolean> {
  try {
    // Check entity service through the accounting server proxy
    // Try to fetch organizations endpoint - if it returns 200 or 401, service is up
    const response = await fetch(`${API_BASE_URL}/api/v1/organizations`, {
      method: 'GET',
      signal: AbortSignal.timeout(20000), // 20 second timeout for slower loads
    })
    // 200 = success, 401 = auth required but service is running, 404 = might be empty but service is up
    return response.ok || response.status === 401 || response.status === 404
  } catch (_error) {
    return false
  }
}

/**
 * Check if the currency rates service is reachable through the proxy
 */
async function checkCurrencyRatesService(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/currencies`, {
      method: 'GET',
      signal: AbortSignal.timeout(20000),
    })
    return response.ok
  } catch (_error) {
    return false
  }
}

/**
 * Check health of all required services
 */
export async function checkServicesHealth(): Promise<ServiceHealth> {
  // Check all services in parallel
  const [isAccountingHealthy, isGeolocationHealthy, isEntityHealthy, isCurrencyRatesHealthy] = await Promise.all([
    checkAccountingServer(),
    checkGeolocationService(),
    checkEntityService(),
    checkCurrencyRatesService(),
  ])

  // All services are required for the app to function properly
  const isHealthy = isAccountingHealthy && isEntityHealthy && isGeolocationHealthy && isCurrencyRatesHealthy

  return {
    isHealthy,
    services: {
      accounting: isAccountingHealthy,
      geolocation: isGeolocationHealthy,
      entity: isEntityHealthy,
      currencyRates: isCurrencyRatesHealthy,
    },
  }
}
